/**
 * Satellite Insights: where in the field the problem is, read from CropGen's
 * class-map picture. Copied verbatim from farmer-app src/services/satelliteImage.js
 * (all but resolveMapTap) and pointInRing from src/geotag/geometry.js, so the
 * Support page names the same patches, in the same places, as the farmer app.
 *
 * `decodeIndexImage` needs a browser canvas; the rest is pure.
 */
import { IMAGE_MATCH, imageSourceFrom, TONE_ORDER } from './satelliteModel';

/* -------- pointInRing (farmer-app src/geotag/geometry.js) -------- */

/** Ray casting; works on {lat,lng} or {x,y} rings. */
export const pointInRing = (point, ring, keyX = 'lng', keyY = 'lat') => {
  const px = point[keyX];
  const py = point[keyY];
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][keyX];
    const yi = ring[i][keyY];
    const xj = ring[j][keyX];
    const yj = ring[j][keyY];
    const straddles = (yi > py) !== (yj > py);
    const intersects = straddles && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

/* -------- picture → patches (farmer-app src/services/satelliteImage.js) -------- */

/** Sample value for a pixel outside the field (transparent). */
export const OUTSIDE = -1;
/** Sample value for a pixel inside the field that matched no class. */
export const UNMATCHED = -2;

const hexToRgb = (hex) => {
  const value = String(hex).trim().replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const n = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/**
 * Every Nth pixel of the picture, matched to a legend class.
 *
 * @param {{rgba: Uint8ClampedArray|Uint8Array, width: number, height: number}} image
 * @param {object} classified the result of classifyLegend for the same answer
 * @param {object} [opts] overrides for IMAGE_MATCH
 * @returns {{grid: Int16Array, cols, rows, step, width, height, shares, unmatchedPct, agreesWithLegend}}
 *   `grid` holds a class index (into `classified.classes`), OUTSIDE or UNMATCHED
 *   per sample; `shares` is label → percent of the opaque samples.
 */
export const classifyPixels = (image, classified, opts = {}) => {
  const { scaleRange, maxResidual, sampleStep, alphaMin, legendAgreementPts } = {
    ...IMAGE_MATCH,
    ...opts,
  };
  const { rgba, width, height } = image;
  const classes = classified.classes.map((c) => {
    const rgb = hexToRgb(c.color);
    return rgb ? { rgb, self: rgb[0] * rgb[0] + rgb[1] * rgb[1] + rgb[2] * rgb[2] } : null;
  });

  const step = Math.max(1, sampleStep);
  const cols = Math.ceil(width / step);
  const rows = Math.ceil(height / step);
  const grid = new Int16Array(cols * rows).fill(OUTSIDE);
  const counts = new Array(classes.length).fill(0);
  const maxResidualSq = maxResidual * maxResidual;
  let opaque = 0;
  let unmatched = 0;

  for (let row = 0; row < rows; row += 1) {
    const y = Math.min(height - 1, row * step + (step >> 1));
    for (let col = 0; col < cols; col += 1) {
      const x = Math.min(width - 1, col * step + (step >> 1));
      const at = (y * width + x) * 4;
      if (rgba[at + 3] < alphaMin) continue;
      opaque += 1;

      const r = rgba[at];
      const g = rgba[at + 1];
      const b = rgba[at + 2];
      let best = UNMATCHED;
      let bestSq = Infinity;

      for (let i = 0; i < classes.length; i += 1) {
        const c = classes[i];
        if (!c || c.self === 0) continue;
        const [cr, cg, cb] = c.rgb;
        const fitted = (r * cr + g * cg + b * cb) / c.self;
        const s = Math.min(scaleRange[1], Math.max(scaleRange[0], fitted));
        const dr = r - s * cr;
        const dg = g - s * cg;
        const db = b - s * cb;
        const sq = dr * dr + dg * dg + db * db;
        if (sq < bestSq) {
          bestSq = sq;
          best = i;
        }
      }

      if (bestSq > maxResidualSq) {
        grid[row * cols + col] = UNMATCHED;
        unmatched += 1;
      } else {
        grid[row * cols + col] = best;
        counts[best] += 1;
      }
    }
  }

  const pct = (n) => (opaque > 0 ? (n / opaque) * 100 : 0);
  const shares = {};
  classified.classes.forEach((c, i) => {
    shares[c.label] = pct(counts[i]);
  });
  const unmatchedPct = pct(unmatched);

  const agreesWithLegend =
    opaque > 0 &&
    unmatchedPct <= legendAgreementPts &&
    classified.classes.every((c, i) => Math.abs(pct(counts[i]) - c.percent) <= legendAgreementPts);

  return { grid, cols, rows, step, width, height, shares, unmatchedPct, agreesWithLegend };
};

/* ------------------------------------------------------------ sectors ---- */

/** The 3 × 3 grid over the field, north-west first, row by row. */
const SECTORS = [
  { key: 'nw', placeKey: 'placeNorthWest' },
  { key: 'n', placeKey: 'placeNorth' },
  { key: 'ne', placeKey: 'placeNorthEast' },
  { key: 'w', placeKey: 'placeWest' },
  { key: 'c', placeKey: 'placeCentre' },
  { key: 'e', placeKey: 'placeEast' },
  { key: 'sw', placeKey: 'placeSouthWest' },
  { key: 's', placeKey: 'placeSouth' },
  { key: 'se', placeKey: 'placeSouthEast' },
];

/** A strip is named when a patch fills sectors along one row or one column. */
const ROW_PLACE = ['placeNorthSide', 'placeMiddleStrip', 'placeSouthSide'];
const COL_PLACE = ['placeWestSide', 'placeCentreStrip', 'placeEastSide'];

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];

const placeKeyFor = (sectorIds, samplesBySector) => {
  if (sectorIds.length === 1) return SECTORS[sectorIds[0]].placeKey;
  if (sectorIds.length >= 5) return 'placeMostOfField';
  const rows = new Set(sectorIds.map((id) => Math.floor(id / 3)));
  const cols = new Set(sectorIds.map((id) => id % 3));
  if (rows.size === 1) return ROW_PLACE[[...rows][0]];
  if (cols.size === 1) return COL_PLACE[[...cols][0]];
  const largest = sectorIds.reduce((a, b) => (samplesBySector[b] > samplesBySector[a] ? b : a));
  return SECTORS[largest].placeKey;
};

const sectorCell = (id, bounds) => {
  const { north, south, east, west } = bounds;
  const row = Math.floor(id / 3);
  const col = id % 3;
  const top = north - ((north - south) * row) / 3;
  const bottom = north - ((north - south) * (row + 1)) / 3;
  const left = west + ((east - west) * col) / 3;
  const right = west + ((east - west) * (col + 1)) / 3;
  return [
    { lat: top, lng: left },
    { lat: top, lng: right },
    { lat: bottom, lng: right },
    { lat: bottom, lng: left },
  ];
};

const neighbours = (id) => {
  const row = Math.floor(id / 3);
  const col = id % 3;
  const out = [];
  if (row > 0) out.push(id - 3);
  if (row < 2) out.push(id + 3);
  if (col > 0) out.push(id - 1);
  if (col < 2) out.push(id + 1);
  return out;
};

/**
 * The named patches of a field: which parts are dry, wet or weak.
 *
 * The picture's box is cut into a 3 × 3 grid. Each part holding enough of the
 * field takes the tone most of its pixels have; neighbouring parts with the
 * same tone (and, for water, the same kind — dry or wet) join into one patch.
 * Only watch and bad parts become patches; a field that is fine everywhere has
 * none, and one whose pixels disagree with its legend has none either.
 *
 * @param {object} pixels the result of classifyPixels
 * @param {object} classified the result of classifyLegend
 * @param {{north, south, east, west}} bounds the picture's extent
 * @param {Array<{lat, lng}>} [ring] the farm boundary; samples outside it are ignored
 * @param {number} [acres] the farm's area, to size each patch
 */
export const sectorPatches = (pixels, classified, bounds, ring, acres) => {
  if (!pixels || !pixels.agreesWithLegend || !bounds) return [];
  const { north, south, east, west } = bounds;
  if (![north, south, east, west].every(Number.isFinite)) return [];

  const { grid, cols, rows, step, width, height } = pixels;
  const { classes } = classified;
  const useRing = Array.isArray(ring) && ring.length >= 3;

  const samples = new Array(9).fill(0);
  const byClass = SECTORS.map(() => new Array(classes.length).fill(0));
  const latSum = new Array(9).fill(0);
  const lngSum = new Array(9).fill(0);
  let fieldSamples = 0;

  for (let row = 0; row < rows; row += 1) {
    const fy = (Math.min(height - 1, row * step + (step >> 1)) + 0.5) / height;
    for (let col = 0; col < cols; col += 1) {
      const value = grid[row * cols + col];
      if (value === OUTSIDE) continue;
      const fx = (Math.min(width - 1, col * step + (step >> 1)) + 0.5) / width;
      const point = { lat: north - fy * (north - south), lng: west + fx * (east - west) };
      if (useRing && !pointInRing(point, ring)) continue;

      const id = Math.min(2, Math.floor(fy * 3)) * 3 + Math.min(2, Math.floor(fx * 3));
      fieldSamples += 1;
      samples[id] += 1;
      latSum[id] += point.lat;
      lngSum[id] += point.lng;
      if (value >= 0) byClass[id][value] += 1;
    }
  }
  if (fieldSamples === 0) return [];

  const minSamples = (IMAGE_MATCH.minSectorSharePct / 100) * fieldSamples;

  /** Each qualifying sector's plurality tone, its kind and its largest class. */
  const sectorInfo = SECTORS.map((sector, id) => {
    if (samples[id] < minSamples) return null;
    const toneCount = { good: 0, watch: 0, bad: 0 };
    const kindCount = { dry: 0, wet: 0 };
    classes.forEach((c, i) => {
      if (!(c.tone in toneCount)) return;
      toneCount[c.tone] += byClass[id][i];
      if (c.kind && c.tone !== 'good') kindCount[c.kind] += byClass[id][i];
    });
    const tone = Object.keys(toneCount).reduce((a, b) =>
      toneCount[b] > toneCount[a] || (toneCount[b] === toneCount[a] && TONE_ORDER[b] > TONE_ORDER[a]) ? b : a
    );
    if (toneCount[tone] === 0 || tone === 'good') return null;
    const kind = kindCount.dry || kindCount.wet ? (kindCount.wet > kindCount.dry ? 'wet' : 'dry') : null;
    let topIndex = -1;
    classes.forEach((c, i) => {
      if (c.tone === tone && (!kind || c.kind === kind) && (topIndex < 0 || byClass[id][i] > byClass[id][topIndex])) {
        topIndex = i;
      }
    });
    return { tone, kind, topIndex };
  });

  /** Neighbouring sectors with the same tone and kind become one patch. */
  const seen = new Set();
  const groups = [];
  sectorInfo.forEach((info, id) => {
    if (!info || seen.has(id)) return;
    const members = [];
    const queue = [id];
    seen.add(id);
    while (queue.length) {
      const current = queue.shift();
      members.push(current);
      neighbours(current).forEach((next) => {
        const other = sectorInfo[next];
        if (!seen.has(next) && other && other.tone === info.tone && other.kind === info.kind) {
          seen.add(next);
          queue.push(next);
        }
      });
    }
    groups.push({ info, members: members.sort((a, b) => a - b) });
  });

  const patches = groups.map(({ info, members }) => {
    const count = members.reduce((total, id) => total + samples[id], 0);
    const sharePct = Math.round((count / fieldSamples) * 1000) / 10;
    const top = members
      .map((id) => sectorInfo[id].topIndex)
      .reduce((best, i) => {
        if (best < 0) return i;
        const total = (index) => members.reduce((t, id) => t + byClass[id][index], 0);
        return total(i) > total(best) ? i : best;
      }, -1);
    return {
      key: members.map((id) => SECTORS[id].key).join('-'),
      sectors: members.map((id) => SECTORS[id].key),
      placeKey: placeKeyFor(members, samples),
      tone: info.tone,
      kind: info.kind,
      topClass: top >= 0 ? classes[top].label : null,
      sharePct,
      acres: Number.isFinite(acres) ? Math.round(((acres * sharePct) / 100) * 100) / 100 : null,
      centre: {
        lat: members.reduce((t, id) => t + latSum[id], 0) / count,
        lng: members.reduce((t, id) => t + lngSum[id], 0) / count,
      },
      cells: members.map((id) => sectorCell(id, bounds)),
    };
  });

  return patches
    .sort((a, b) => TONE_ORDER[b.tone] - TONE_ORDER[a.tone] || b.sharePct - a.sharePct)
    .slice(0, IMAGE_MATCH.maxPatches)
    .map((patch, i) => ({ ...patch, letter: LETTERS[i] }));
};

/* ------------------------------------------------------------ browser ---- */

/**
 * Decodes the provider's picture into raw pixels. Browser only.
 *
 * Resolves null on anything that goes wrong — an unrecognised format, a
 * decode failure, no canvas — and the caller treats that exactly like a
 * picture that could not be trusted: no patches, legend rating unchanged.
 */
export const decodeIndexImage = (imageBase64) =>
  new Promise((resolve) => {
    const src = imageSourceFrom(imageBase64);
    if (!src || typeof Image === 'undefined' || typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const img = new Image();
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const draw = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          done(null);
          return;
        }
        context.drawImage(img, 0, 0);
        const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
        done({ rgba: data, width: canvas.width, height: canvas.height });
      } catch (err) {
        done(null);
      }
    };
    img.onerror = () => done(null);
    img.src = src;
    // decode() does the decoding off the main thread where the browser has it;
    // older browsers without it take the plain onload path.
    if (typeof img.decode === 'function') {
      img.decode().then(draw, () => done(null));
    } else {
      img.onload = draw;
    }
  });

/**
 * One answer's picture, read: its named patches and the classified pixels
 * behind them. The pixels are what a tap on the map is looked up in (classAt).
 *
 * @param {object} result a normaliseIndex result carrying `imageBase64` and `bounds`
 * @param {object} classified classifyLegend of the same answer
 * @param {Array<{lat, lng}>} ring the farm boundary
 * @param {number} acres the farm's area
 * @returns {Promise<{ patches: Array, pixels: object|null }>}
 */
export const analyseIndexImageFull = async (result, classified, ring, acres) => {
  const none = { patches: [], pixels: null };
  if (!result || !result.imageBase64 || !result.bounds || !classified?.hasData) return none;
  const image = await decodeIndexImage(result.imageBase64);
  if (!image) return none;
  const pixels = classifyPixels(image, classified);
  return { patches: sectorPatches(pixels, classified, result.bounds, ring, acres), pixels };
};

/**
 * The patches for one normalised index answer, from picture to named places.
 * The same as analyseIndexImageFull without the pixels.
 */
export const analyseIndexImage = async (result, classified, ring, acres) =>
  (await analyseIndexImageFull(result, classified, ring, acres)).patches;

/* ---------------------------------------------------------------- taps ---- */

/**
 * The class of the picture at one point: what a farmer's tap landed on.
 *
 * Uses the same sample grid as sectorPatches, so a tap reads the nearest
 * sampled pixel. Pixels that could not be trusted are not read at all: when
 * the picture disagrees with its legend, nothing is returned.
 *
 * @param {object} pixels the result of classifyPixels
 * @param {object} classified classifyLegend of the same answer
 * @param {{north, south, east, west}} bounds the picture's extent
 * @param {{lat, lng}} point
 * @returns {{ label, tone, kind } | null} null outside the picture, on a
 *   transparent or unmatched pixel, or when the picture is not trusted
 */
export const classAt = (pixels, classified, bounds, point) => {
  if (!pixels || !pixels.agreesWithLegend || !classified || !bounds || !point) return null;
  const { north, south, east, west } = bounds;
  if (![north, south, east, west, point.lat, point.lng].every(Number.isFinite)) return null;
  const fx = (point.lng - west) / (east - west);
  const fy = (north - point.lat) / (north - south);
  if (fx < 0 || fx >= 1 || fy < 0 || fy >= 1) return null;

  const { grid, cols, rows, step, width, height } = pixels;
  const col = Math.min(cols - 1, Math.floor((fx * width) / step));
  const row = Math.min(rows - 1, Math.floor((fy * height) / step));
  const value = grid[row * cols + col];
  if (value < 0) return null;

  const cls = classified.classes[value];
  return cls ? { label: cls.label, tone: cls.tone, kind: cls.kind || null } : null;
};
